import React, { useRef } from 'react';
import { Pressable, View } from 'react-native';
import { Link } from 'expo-router';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { Section, useInteractive, useLanding } from '@/components/Landing/LandingKit';
import { fluid } from '@/components/Landing/landingTokens';

import { apiPalette } from './apiTokens';

export function useApiDesign() {
  const landing = useLanding();
  return { ...landing, c: apiPalette(landing.isDark), compact: landing.width < 1000, mobile: landing.width < 600 };
}

export function ApiSection({ label, background, children, contentStyle }) {
  const { width } = useApiDesign();
  return <Section label={label} max={1248} background={background} contentStyle={{
    paddingTop: fluid(width, 60, 96), paddingBottom: fluid(width, 60, 96), ...contentStyle,
  }}>{children}</Section>;
}

export function ApiSplit({ children, style }) {
  const { compact, width } = useApiDesign();
  return <View style={{ flexDirection: compact ? 'column' : 'row', gap: compact ? 36 : fluid(width, 36, 76), ...style }}>{children}</View>;
}

export function ApiEyebrow({ children, style }) {
  const { c } = useApiDesign();
  return <Text weight="semibold" style={{ color: c.secondary, fontSize: 13, lineHeight: 20, letterSpacing: 0.3, marginBottom: 16, ...style }}>{children}</Text>;
}

export function ApiTitle({ children, style }) {
  const { c, width } = useApiDesign();
  const size = fluid(width, 34, 48);
  return <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: size, lineHeight: Math.round(size * 1.1), letterSpacing: -size * 0.04, whiteSpace: 'pre-line', ...style }}>{children}</Text>;
}

export function ApiBody({ children, style }) {
  const { c } = useApiDesign();
  return <Text tag="p" style={{ color: c.secondary, fontSize: 17, lineHeight: 28, ...style }}>{children}</Text>;
}

export function ApiLink({ href, children, primary = false, style, label }) {
  const { c } = useApiDesign();
  const { hovered, handlers } = useInteractive();
  return (
    <Link href={href} asChild>
      <Pressable {...handlers} accessibilityRole="link" accessibilityLabel={label} style={{
        flexDirection: 'row', alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-start',
        gap: 10, minHeight: 46, paddingHorizontal: primary ? 23 : 4, borderRadius: 999,
        backgroundColor: primary ? c.ink : 'transparent', opacity: hovered ? 0.75 : 1, ...style,
      }}>
        <Text weight="medium" style={{ color: primary ? c.page : c.ink, fontSize: 15, lineHeight: 22 }}>{children}</Text>
        <Icon name="arrow-right" size={12} color={primary ? c.page : c.ink} />
      </Pressable>
    </Link>
  );
}

/** Segmented control with roving keyboard focus and linked tab panels. */
export function ApiTabs({ id, label, items, activeId, onChange, dark = false }) {
  const { c, mobile } = useApiDesign();
  const refs = useRef([]);
  const selectWithKey = (event, index) => {
    const last = items.length - 1;
    const next = { ArrowRight: (index + 1) % items.length, ArrowLeft: (index + last) % items.length, Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    onChange(items[next].id);
    refs.current[next]?.focus();
  };
  return (
    <View accessibilityRole="tablist" accessibilityLabel={label} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
      {items.map((item, index) => (
        <Pressable key={item.id} ref={node => { refs.current[index] = node; }}
          nativeID={`${id}-tab-${item.id}`} accessibilityRole="tab" accessibilityLabel={item.label}
          aria-selected={activeId === item.id} aria-controls={activeId === item.id ? `${id}-panel-${item.id}` : undefined}
          tabIndex={activeId === item.id ? 0 : -1} onKeyDown={event => selectWithKey(event, index)}
          onPress={() => onChange(item.id)} style={({ hovered }) => ({
            paddingHorizontal: mobile ? 12 : 16, minHeight: 44, justifyContent: 'center', borderRadius: 10,
            backgroundColor: activeId === item.id ? (dark ? '#36363A' : c.surface) : hovered ? (dark ? '#29292D' : c.hover) : 'transparent',
          })}>
          <Text weight="medium" style={{ fontSize: 13, lineHeight: 20, color: activeId === item.id ? (dark ? '#F5F5F7' : c.ink) : (dark ? '#A7A7B0' : c.secondary) }}>{mobile ? item.shortLabel || item.label : item.label}</Text>
        </Pressable>
      ))}
    </View>
  );
}
